<script>
  import { createEventDispatcher } from "svelte";
  import { Loading } from "carbon-components-svelte";
  import Modal from "../../../../lib/components/Modal.svelte";

  export let open = false;
  export let loading = false;
  export let review = null;
  export let itemLabel = "Window title";
  // While the selected rule is being applied, shown instead of an empty dialog.
  export let applying = false;

  // Lowercase inside a sentence, but keep abbreviations like "UI" as they are.
  $: labelInText = /^[A-Z]{2}/.test(itemLabel) ? itemLabel : itemLabel.toLowerCase();

  // The masked example of the source entry, split into plain text and type chips,
  // so users see what the rule does instead of the raw regular expression.
  $: exampleParts = splitMasked(
    review?.source?.anonymizedString ?? review?.source?.anonymized ?? "",
  );

  function splitMasked(value) {
    const text = String(value || "").replaceAll("<<<space>>>", " ");
    const parts = [];
    let last = 0;
    for (const match of text.matchAll(/<<<([^>]+)>>>/g)) {
      if (match.index > last) parts.push({ text: text.slice(last, match.index) });
      parts.push({ type: match[1] });
      last = match.index + match[0].length;
    }
    if (last < text.length) parts.push({ text: text.slice(last) });
    return parts;
  }

  const dispatch = createEventDispatcher();
  const close = () => dispatch("close");
</script>

<Modal
  bind:open
  actions
  autowidth
  xxl
  modalHeading={`Review ${labelInText} rule`}
  secondaryButtonText="Cancel"
  primaryButtonText="Apply selected"
  primaryButtonNotAllow={loading || applying || !review?.preview}
  primaryButtonValidations={loading || applying || !review?.preview}
  secondaryButtonAction={close}
  primaryButtonAction={() => dispatch("apply")}
  on:close={close}
>
  {#if loading}
    <div class="rule-loading">
      <Loading small withOverlay={false} description="Generating rule preview" />
      <span>Generating rule and checking similar entries…</span>
    </div>
  {:else if applying}
    <div class="rule-loading">
      <Loading small withOverlay={false} description="Applying rule" />
      <span>Applying rule…</span>
    </div>
  {:else}
    <div class="rule-regex">
      <span>This rule masks {labelInText}s like:</span>
      <div class="rule-example">
        {#each exampleParts as part}
          {#if part.type}
            <span class="rule-chip">{part.type}</span>
          {:else}
            <span class="rule-text">{part.text}</span>
          {/if}
        {/each}
      </div>
    </div>
    <p class="rule-description">
      Review the matching entries. Unselected entries are excluded from this rule.
    </p>
    <div class="rule-candidates">
      <div class="rule-list-header">
        <span></span>
        <strong>{itemLabel}</strong>
        <strong>Similarity</strong>
      </div>
      {#each review?.preview?.candidates || [] as candidate}
        <label class="rule-candidate" class:problematic={candidate.problematic}>
          <input
            type="checkbox"
            bind:checked={candidate.selected}
            disabled={candidate.id === review?.preview?.source_id}
          />
          <span class="rule-title" title={candidate.windowtitle}>
            {candidate.windowtitle}
            {#if candidate.applicationname}<small>{candidate.applicationname}</small>{/if}
            {#if candidate.reason}<small class="problem-reason">{candidate.reason}</small>{/if}
          </span>
          <strong class="rule-similarity">{Math.round((candidate.similarity || 0) * 100)}%</strong>
        </label>
      {/each}
    </div>
  {/if}
</Modal>

<style>
  .rule-description { margin: 1rem 0; }
  .rule-loading { display: flex; min-height: 16rem; flex-direction: column; align-items: center; justify-content: center; gap: 1rem; }
  .rule-regex, .rule-candidates { display: flex; flex-direction: column; }
  .rule-regex { gap: 0.5rem; }
  .rule-regex span { font-weight: 600; }
  .rule-example { display: flex; flex-wrap: wrap; align-items: center; gap: 0.25rem; padding: 0.75rem; border: 1px solid #d9d9d9; border-radius: 0.25rem; background: #f4f4f4; }
  .rule-text { white-space: pre-wrap; overflow-wrap: anywhere; }
  .rule-chip { padding: 0 0.375rem; border-radius: 0.25rem; background: var(--pax-blue, #a6c8ff); font-size: 0.75rem; font-weight: 600; line-height: 1.5rem; }
  .rule-candidates { gap: 0.25rem; max-height: 28rem; overflow-y: auto; }
  .rule-list-header, .rule-candidate { display: grid; grid-template-columns: 1.5rem minmax(0, 1fr) 6rem; align-items: center; gap: 0.75rem; padding: 0.5rem; }
  .rule-list-header { position: sticky; top: 0; z-index: 1; border-bottom: 1px solid #d9d9d9; background: #fff; }
  .rule-list-header strong:last-child, .rule-similarity { text-align: right; }
  .rule-candidate { background: #f4f4f4; cursor: pointer; }
  .rule-candidate.problematic { border-left: 3px solid #da1e28; }
  .rule-title { display: flex; min-width: 0; flex-direction: column; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .rule-title small { color: #525252; }
  .rule-title .problem-reason { color: #da1e28; }
  .rule-similarity { font-variant-numeric: tabular-nums; }
</style>
